import * as React from "react";
import { inject, observer } from "mobx-react";
import { LabelGroup, LabelStore } from "Stores/labelStore";
import { toJS } from "mobx";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { CSSProperties } from "react";
import { CrudModal } from "Components/Common/CrudModal";
import { AppStore } from "Stores/appStore";
import * as _ from "lodash";
import {Utils} from "../../common/utils";
import PageTitle from "Components/Common/PageTitle";

interface IconButtonProps<T>{
    data: T
    onClick: (t: T) => void
    className: string
}

class IconButton<T> extends React.Component<IconButtonProps<T>>{

    handleClick = () => {
        this.props.onClick(this.props.data);
    };

    render(){
        return(<i onClick={ this.handleClick } className={ this.props.className } tabIndex={0}/>);
    }
}

interface LabelGroupsProps {
    labelStore?: LabelStore
    appStore?: AppStore
}

interface LabelGroupsState {
    showModal: boolean
    selectedGroup?: LabelGroup
    formGroupName: string
}

@inject("labelStore", "appStore")
@observer
export class LabelGroups extends React.Component<LabelGroupsProps, LabelGroupsState>{

    constructor(props) {
        super(props);
        this.state = {
            showModal: false,
            selectedGroup: undefined,
            formGroupName: "",
        };
    }

    componentDidMount() {
        Utils.setViewName("LabelGroupList", "Labels Groups");
        this.props.labelStore!.fetchLabelGroups();
    }

    resetState = () => {
        this.setState({showModal: false, selectedGroup: undefined, formGroupName: ""});
    };

    handleEditClick = (group: LabelGroup) => {
        this.setState({ showModal: true, selectedGroup: group, formGroupName: group.name });
    };

    handleDeleteClick = (group: LabelGroup) => {
        if(!this.props.appStore) return;

        this.props.appStore.showConfirmModal({
            // These divs have keys to stop react warning "Each child in a list should have a unique "key" prop."
            element: (
            <div>
                <div key="line-1">Are you sure you want to delete this label group?</div>
                <div key="line-2">This will delete all {group.labels.length} labels in this group</div>
            </div>),
            accept: () => {
                this.props.labelStore!.deleteLabelGroup(group.id).then(() => {
                    this.props.labelStore!.fetchLabelGroups();
                });
            },
            cancel: () => {},
            showActions: true,
        });
    };

    handleModalSave = () => {
        if(!this.props.labelStore || (!this.state.selectedGroup && this.props.labelStore.labelGroups.length >= 3)) return;

        const data = {name: this.state.formGroupName};
        (this.state.selectedGroup
            ? this.props.labelStore.updateLabelGroup((this.state.selectedGroup! as LabelGroup).id, data)
            : this.props.labelStore.createLabelGroup(data)
        ).then(() => {
            this.props.labelStore!.fetchLabelGroups().then(() => {
                this.resetState();
            });
        });
    };

    handleModalClose = () => {
        this.resetState();
    };

    handleDragEnd = (ev: DropResult) => {
        if(!this.props.labelStore || !ev.destination) return;
        const nextGroups = toJS(this.props.labelStore.labelGroups);
        // pull the group that has been moved and splice it into its new position
        nextGroups.splice(ev.destination.index, 0, ..._.pullAt(nextGroups, ev.source.index));
        this.props.labelStore.labelGroups = nextGroups;
        this.props.labelStore.updateLabelGroup(parseInt(ev.draggableId),{ordering: ev.destination.index}).then(() => {
            this.props.labelStore!.fetchLabelGroups();
        });
    };

    handleNameChange = (ev: any) => {
        this.setState({formGroupName: ev.target.value});
    };

    handleAddClick = () => {
        this.setState({showModal: true, selectedGroup: undefined, formGroupName: ""});
    };

    isValid = () => {
        if(this.state.selectedGroup && this.state.selectedGroup.id){
            const selectedId = this.state.selectedGroup.id;

            const groups = toJS(this.props.labelStore!.labelGroups);
            return groups.findIndex((g: LabelGroup) => g.name.toLowerCase() === this.state.formGroupName.toLowerCase() && g.id !== selectedId) < 0;
        }
        return toJS(this.props.labelStore!.labelGroups).findIndex((g: LabelGroup) => g.name.toLowerCase() === this.state.formGroupName.toLowerCase()) < 0;
    };

    renderCreateButton = () => {
        if(this.props.labelStore && this.props.labelStore!.labelGroups.length < 3){
            return (
                <div className="actions">
                    <Button onClick={ this.handleAddClick } data-test-id="label-groups-create-btn">create label group</Button>
                </div>);
        }
        return null;
    };

    renderModalForm = () => {
        return (<React.Fragment>
            <Row>
                <Col>
                    <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            className="no-icon"
                            onChange={ this.handleNameChange }
                            value={ this.state.formGroupName }
                            placeholder="Enter a name"
                            isInvalid={ !this.isValid() }
                            />
                        <Form.Control.Feedback type="invalid" >There is already a group with that name</Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
        </React.Fragment>);
    };

    renderDraggableList = () => {
        if(this.props.labelStore && this.props.labelStore.isProcessing){
            return (<Spinner animation="border"/>);
        }
        return toJS(this.props.labelStore!.labelGroups).map((group, index) => (
            <Draggable
                key={group.id}
                draggableId={group.id.toString()}
                index={index}>
                {(dragProvided, dragSnapshot) => (
                    <div
                        key={group.id}
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={dragProvided.draggableProps.style as CSSProperties}
                        className={`row no-gutters item align-items-center ${dragSnapshot.isDragging ? "dragging" :""}`}>
                        <Col md={1}><span className="drag-icon">&#9776;</span></Col>
                        <Col md={8}>{group.name}</Col>
                        <Col md={3} className="actions">
                            <IconButton data={group} onClick={this.handleEditClick} className="icon icon-pencil-square-o action-button" data-test-id={`label-group-edit-${group.id}`}/>
                            <IconButton data={group} onClick={this.handleDeleteClick} className="icon icon-times action-button" data-test-id={`label-group-delete-${group.id}`}/>
                        </Col>
                    </div>
                )}
            </Draggable>
        ));
    };

    renderDragDropContainer = () => {
        return (
            <DragDropContext onDragEnd={this.handleDragEnd}>
                <Droppable droppableId="droppableSource">
                    {(dropProvided, dropSnapshot) => (
                        <div
                            ref={dropProvided.innerRef}
                            className={`list ${dropSnapshot.isDraggingOver ? "dragging" : ""}`}>
                            <div className="item header row no-gutters align-items-center">
                                <Col md={1}/>
                                <Col md={8}>Name</Col>
                                <Col md={3} className="actions">Actions</Col>
                            </div>
                            <div className={this.props.labelStore!.isProcessing ? "loading":""}>{ this.renderDraggableList() }</div>
                            { dropProvided.placeholder }
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    };

    render(){
        return(
            <React.Fragment>
                <PageTitle pageTitle={ "Label Groups" } />
                <Row className="section-action-bar">
                    <Col md={12} lg={6} className="title">
                        <h3>Label Groups</h3>
                    </Col>
                    <Col>
                        {this.renderCreateButton()}
                    </Col>
                </Row>
                <div className="label-group-container row justify-content-center" >
                    { !this.props.labelStore || !this.props.labelStore.labelGroups
                        ? <Spinner animation="border"/>
                        : this.renderDragDropContainer() }
                    { this.state.showModal
                        ? <CrudModal
                            isDisabled={ !this.props.labelStore || this.props.labelStore.isProcessing || !this.state.formGroupName || !this.isValid() }
                            renderForm={ this.renderModalForm }
                            isEdit={ !!this.state.selectedGroup }
                            showSpinner={ !this.props.labelStore || this.props.labelStore.isProcessing }
                            onSave={ this.handleModalSave }
                            onClose={ this.handleModalClose }
                            typeLabel="Label Group"
                            />
                        : null }
                </div>
            </React.Fragment>);
    }
}
